import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { ActionNeeded } from "@/components/ActionNeeded";
import { toast } from "sonner";
import { Loader2, Inbox, FileCheck2, Sparkles, X, Users, ShieldCheck, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { MessageDialog } from "@/components/SuggestedMessages";
import { Info } from "@/components/Info";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DOC_TYPE_FID = "fld8bFv7XyMaRQaoh";
const BELONGS = ["Borrower", "Co-borrower", "Company", "Bank", "Property / Seller", "Other"];
const SOURCES = ["WhatsApp", "Email", "Manual upload", "Partner"];
const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

export function IncomingFiles({ recordId, selects, onFiled, refreshKey }) {
  const [data, setData] = useState(null);
  const [showTeam, setShowTeam] = useState(false);
  const [fileCard, setFileCard] = useState(null);
  const typeOpts = selects?.[DOC_TYPE_FID] || [];

  const load = async () => {
    try { const r = await api.get(`/cases/${recordId}/documents/incoming`); setData(r.data); }
    catch { setData({ inbox: [], team: [] }); }
  };
  useEffect(() => { load(); }, [recordId, refreshKey]); // eslint-disable-line
  // P0: AI suggestions run in the background — poll while any card is still processing.
  const anyProcessing = !!data?.inbox?.some((c) => c.suggest_processing);
  useEffect(() => {
    if (!anyProcessing) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [anyProcessing]); // eslint-disable-line

  if (!data) return null;
  const { inbox = [], team = [] } = data;

  return (
    <div className="rounded-2xl border border-[#E2E2E2] bg-white shadow-sm overflow-hidden" data-testid="incoming-files">
      <div className="px-5 py-3 bg-[#FF4802]/5 border-b border-[#E2E2E2] flex items-center gap-2">
        <Inbox className="w-4 h-4 text-[#FF4802]" />
        <h3 className="text-sm font-medium text-[#333333]">Incoming files <Info k="incoming_files" /></h3>
        <span className="ml-1 text-xs font-semibold bg-[#FF4802] text-white rounded-full px-2 py-0.5" data-testid="incoming-count">{inbox.length}</span>
      </div>
      <div className="divide-y divide-[#F7F7F7]">
        {data?.action_needed?.length > 0 && <div className="px-5 py-2"><ActionNeeded items={data.action_needed} onChanged={load} /></div>}
        {inbox.length === 0 && <div className="px-5 py-6 text-sm text-[#6B7280]">No files waiting to file. Files the customer or partner shares in the linked WhatsApp group will appear here automatically.</div>}
        <ReadyToFile recordId={recordId} inbox={inbox} onFiled={() => { load(); onFiled?.(); }} />
        <AutoFiled recordId={recordId} typeOpts={typeOpts} refreshKey={inbox.length} onChanged={() => { load(); onFiled?.(); }} />
        {inbox.map((c) => <IncomingCard key={c.card_id} card={c} recordId={recordId} onSuggested={load} onFile={() => setFileCard(c)} onDismiss={load} />)}
      </div>
      {team.length > 0 && (
        <div className="border-t border-[#E2E2E2]">
          <button onClick={() => setShowTeam((s) => !s)} data-testid="toggle-team-files" className="w-full px-5 py-2.5 flex items-center gap-2 text-xs text-[#6B7280] hover:bg-[#F7F7F7]">
            <Users className="w-3.5 h-3.5" /> Shared by team ({team.length}) {showTeam ? "▲" : "▼"} <Info k="shared_by_team" />
          </button>
          {showTeam && <div className="divide-y divide-[#F7F7F7]">{team.map((c) => <IncomingCard key={c.card_id} card={c} recordId={recordId} onSuggested={load} onFile={() => setFileCard(c)} onDismiss={load} />)}</div>}
        </div>
      )}
      {fileCard && <FileDialog card={fileCard} recordId={recordId} typeOpts={typeOpts}
        onClose={() => setFileCard(null)} onDone={() => { setFileCard(null); load(); onFiled?.(); }} />}
    </div>
  );
}

function AutoFiled({ recordId, typeOpts = [], refreshKey, onChanged }) {
  const [d, setD] = useState(null);
  const load = () => api.get(`/cases/${recordId}/documents/auto-filed`).then((r) => setD(r.data)).catch(() => setD(null));
  useEffect(() => { load(); }, [recordId, refreshKey]); // eslint-disable-line
  if (!d) return null;
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); onChanged?.(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const toggle = () => act(() => api.post(`/cases/${recordId}/documents/autofile-toggle`, { on: !d.autofile_on }), d.autofile_on ? "Auto-file off for this case" : "Auto-file on");
  return (
    <div className="px-5 py-3 bg-[#38A2FF]/5 border-b border-[#E2E2E2]" data-testid="auto-filed">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-medium text-[#1d6fb8]">Auto-filed ({d.items.length}) <Info k="autofile" /></div>
        <label className="flex items-center gap-1.5 text-[11px] text-[#6B7280] cursor-pointer"><input type="checkbox" checked={d.autofile_on} onChange={toggle} data-testid="autofile-toggle" className="accent-[#38A2FF]" /> Auto-file {d.autofile_on ? "on" : "off"}</label>
      </div>
      {d.items.length === 0 && <div className="text-[11px] text-[#6B7280]">Confident files (≥ {Math.round((d.settings?.threshold || 0.85) * 100)}%) are filed automatically and listed here for review.</div>}
      {d.items.map((i) => (
        <div key={i.id} className="flex items-center gap-2 text-xs text-[#333333] py-1" data-testid={`auto-filed-${i.id}`}>
          <FileCheck2 className="w-3.5 h-3.5 text-[#1d6fb8]" />
          <span className="flex-1 truncate">{i.label} → <b>{i.belongs_to}</b>{i.password_needed ? <span className="text-[#CA1212]"> · 🔒 Password needed</span> : null}{i.profile_set ? <span className="text-[#159A50]"> · profile set: {i.profile_set.value}</span> : null}{i.new_person ? <span className="text-[#92600a]"> · new person: {i.new_person}</span> : null} <span className="text-[#C5C4C4]">{Math.round((i.confidence || 0) * 100)}%</span></span>
          {i.password_needed && <button onClick={async () => { const p = window.prompt("PDF password (saved on the Documents row only)"); if (!p) return; act(() => Promise.all(i.doc_row_ids.map((rid) => api.post(`/cases/${recordId}/documents/${rid}/status`, { status: "Received", reason: null, password: p }))), "Password saved"); }} data-testid={`auto-add-password-${i.id}`} className="text-[11px] border border-[#CA1212]/40 text-[#CA1212] rounded-lg px-2 py-0.5">Add password</button>}
          <select defaultValue="" onChange={(e) => { const v = e.target.value; e.target.value = ""; if (v) act(() => api.post(`/cases/${recordId}/documents/auto-filed/${i.id}/move`, { document_type: v }), `Moved to ${v}`); }} data-testid={`auto-move-${i.id}`} className="text-[11px] border border-[#E2E2E2] rounded-lg px-1.5 py-0.5 bg-white"><option value="">Move to…</option>{typeOpts.map((o) => <option key={o} value={o}>{o}</option>)}</select>
          <button onClick={() => act(() => api.post(`/cases/${recordId}/documents/auto-filed/${i.id}/move`, { belongs_to: i.belongs_to === "Borrower" ? "Co-borrower" : "Borrower" }), "Moved to the other person")} data-testid={`auto-wrong-person-${i.id}`} className="text-[11px] border border-[#E2E2E2] rounded-lg px-2 py-0.5 hover:border-[#F5B71D]">Wrong person</button>
          <button onClick={() => act(() => api.post(`/cases/${recordId}/documents/auto-filed/${i.id}/undo`), "Undone — file is back in Incoming")} data-testid={`auto-undo-${i.id}`} className="text-[11px] text-[#CA1212] underline">Undo</button>
        </div>
      ))}
    </div>
  );
}

function ReadyToFile({ recordId, inbox, onFiled }) {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/cases/${recordId}/documents/ready-to-file`).then((r) => { setItems(r.data.items || []); setSel(Object.fromEntries((r.data.items || []).map((i) => [i.card_id, true]))); }).catch(() => setItems([]));
  }, [recordId, inbox.map((c) => c.card_id + (c.suggestion ? "s" : "")).join(",")]); // eslint-disable-line
  if (items.length === 0) return null;
  const chosen = items.filter((i) => sel[i.card_id]).map((i) => i.card_id);
  const fileAll = async () => {
    if (!chosen.length) return;
    if (!window.confirm(`File ${chosen.length} document${chosen.length === 1 ? "" : "s"} as suggested? This creates Documents rows in Airtable.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/cases/${recordId}/documents/file-bulk`, { card_ids: chosen });
      toast.success(`Filed ${r.data.filed.length}${r.data.failed.length ? ` · ${r.data.failed.length} failed` : ""}`);
      onFiled();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk filing failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="px-5 py-3 bg-[#159A50]/5" data-testid="ready-to-file">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-medium text-[#0f7d40]">{items.length} ready to file — AI is confident about type and applicant</div>
        <button onClick={fileAll} disabled={busy || !chosen.length} data-testid="ready-file-all" className="text-xs bg-[#159A50] text-white rounded-lg px-3 py-1.5 font-medium disabled:opacity-50">{busy ? "Filing…" : `File ${chosen.length} selected`}</button>
      </div>
      <div className="space-y-1">
        {items.map((i) => (
          <label key={i.card_id} className="flex items-center gap-2 text-xs text-[#333333]" data-testid={`ready-row-${i.card_id}`}>
            <input type="checkbox" className="accent-[#159A50] w-4 h-4" checked={!!sel[i.card_id]} onChange={(e) => setSel({ ...sel, [i.card_id]: e.target.checked })} data-testid={`ready-check-${i.card_id}`} />
            <span className="truncate max-w-[220px]">{i.count ? `${i.count} photos` : i.name}</span>
            {i.documents?.length > 1
              ? <span className="text-[#159A50]" data-testid={`ready-multi-${i.card_id}`}>→ {i.documents.map((d) => `${d.pages?.length ? `p${d.pages.join(",")} ` : ""}${d.document_type} · ${d.belongs_to}`).join(" / ")} <span className="text-[#6B7280]">({i.documents.length} rows)</span></span>
              : <span className="text-[#159A50]">→ {i.document_type} · {i.belongs_to}{i.period ? ` · ${i.period}` : ""}</span>}
            <span className="text-[#C5C4C4]">{Math.round((i.confidence || 0) * 100)}%</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function IncomingCard({ card, recordId, onSuggested, onFile, onDismiss }) {
  const [busy, setBusy] = useState(false);
  const autoRan = useRef(false);
  const s = card.suggestion;
  const thumb = `${BACKEND}/api/cases/${recordId}/documents/incoming/${card.card_id}/preview`;
  const suggest = async (silent) => {
    if (!silent) setBusy(true);
    try {
      const r = await api.post(`/cases/${recordId}/documents/incoming/suggest`, { card_id: card.card_id });
      if (r.data.processing && !silent) toast.info("Processing… the AI suggestion will appear here shortly");
      onSuggested();
    }
    catch (e) { if (!silent) toast.error(e?.response?.data?.detail || "Suggestion failed"); }
    finally { if (!silent) setBusy(false); }
  };
  useEffect(() => { if (!autoRan.current && !s) { autoRan.current = true; suggest(true); } }, []); // eslint-disable-line
  const dismiss = async () => {
    try { await api.post(`/cases/${recordId}/documents/dismiss`, { card_id: card.card_id }); toast.success("Dismissed"); onDismiss(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="px-5 py-3 flex items-start gap-3" data-testid={`incoming-card-${card.card_id}`}>
      <button onClick={() => window.open(`${thumb}?full=1`, "_blank")} title="Open preview" data-testid={`preview-${card.card_id}`}
        className="w-12 h-12 rounded-lg border border-[#E2E2E2] bg-[#F7F7F7] overflow-hidden flex-shrink-0 flex items-center justify-center">
        <img src={thumb} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      </button>
      <div className="flex items-center justify-between gap-3 flex-1 min-w-0">
        <div className="min-w-0">
          <div className="text-sm text-[#333333] font-medium truncate">
            {card.message_type === "image_burst" ? `${card.count} photos` : (card.original_name || card.message_type)}
          </div>
          <div className="text-[11px] text-[#6B7280]">{card.source && card.source !== "whatsapp" && <span data-testid={`source-${card.card_id}`} className="mr-1 px-1.5 py-0.5 rounded bg-[#38A2FF]/10 text-[#38A2FF] font-medium">{card.source_label}</span>}{card.sender} · {fmt(card.when)}{card.caption ? ` · “${card.caption}”` : ""}{card.person_hint ? ` · for ${card.person_hint}` : ""}{card.gmail_link ? <> · <a href={card.gmail_link} target="_blank" rel="noreferrer" className="text-[#38A2FF] hover:underline">Open in Gmail</a></> : null}</div>
          {!s && card.suggest_processing && <div className="text-[11px] mt-1 text-[#6B7280] flex items-center gap-1" data-testid={`suggest-processing-${card.card_id}`}><Loader2 className="w-3 h-3 animate-spin text-[#FF4802]" /> Analysing… AI is reading the file</div>}
          {!s && !card.suggest_processing && !card.suggest_error && card.suggest_status === "queued" && <div className="text-[11px] mt-1 text-[#6B7280]" data-testid={`suggest-queued-${card.card_id}`}>Queued for analysis…</div>}
          {!s && !card.suggest_processing && card.suggest_error && <div className="text-[11px] mt-1 text-[#CA1212]" data-testid={`suggest-error-${card.card_id}`}>{card.suggest_error}</div>}
          {s && (
            <div className="text-[11px] mt-1" data-testid={`suggestion-${card.card_id}`}>
              {s.needs_password
                ? <span className="text-[#CA1212] font-medium">🔒 Password needed</span>
                : s.duplicate_of
                  ? <span className="text-[#159A50]">Already received on {fmt(s.duplicate_of)}</span>
                  : <span className="text-[#159A50]">{s.document_type || "?"} · {s.belongs_to || "?"}{s.period_or_year ? " · " + s.period_or_year : ""}{s.confidence ? ` (${Math.round(s.confidence * 100)}%)` : ""}</span>}
              {s.documents?.length > 1 && <span className="text-[#92600a]" data-testid={`suggestion-multi-${card.card_id}`}> — {s.documents.length} documents in this file: {s.documents.map((d) => `p${(d.pages || []).join(",")} ${d.document_type}`).join(", ")}</span>}
              {s.notes ? <span className="text-[#6B7280]"> — {s.notes}</span> : null}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button onClick={() => suggest(false)} disabled={busy} data-testid={`suggest-${card.card_id}`} className="flex items-center gap-1 text-xs border border-[#E2E2E2] rounded-lg px-2.5 py-1.5 text-[#6B7280] hover:border-[#FF4802]">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 text-[#FF4802]" />} AI</button>
          <button onClick={onFile} data-testid={`file-card-${card.card_id}`} className="text-xs bg-[#FF4802] text-white rounded-lg px-3 py-1.5 font-medium hover:bg-[#e03e00]">File</button>
          <button onClick={dismiss} title="Not a document" data-testid={`dismiss-${card.card_id}`} className="p-1.5 rounded-lg border border-[#E2E2E2] text-[#6B7280] hover:bg-[#CA1212]/5 hover:text-[#CA1212]"><X className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

function FileDialog({ card, recordId, typeOpts, onClose, onDone }) {
  const s = card.suggestion || {};
  const isPdf = (card.mimetype || "").includes("pdf") || (card.original_name || "").toLowerCase().endsWith(".pdf");
  const [type, setType] = useState(s.document_type || "");
  const [belongs, setBelongs] = useState(s.belongs_to || (card.chat_type === "partner" ? "Bank" : "Borrower"));
  const [period, setPeriod] = useState(s.period_or_year || "");
  const [source, setSource] = useState(card.chat_type === "partner" ? "Partner" : "WhatsApp");
  const [merge, setMerge] = useState(true);
  const [password, setPassword] = useState(s.prefill_password || "");
  const [saving, setSaving] = useState(false);
  const isBurst = card.message_type === "image_burst";
  const multi = (s.documents || []).length > 1 ? s.documents : [];
  const [extraSel, setExtraSel] = useState(() => multi.slice(1).map(() => true));

  const save = async () => {
    if (!type) { toast.error("Pick a document type"); return; }
    setSaving(true);
    try {
      const extra_documents = multi.slice(1).filter((_, i) => extraSel[i]).map((d) => ({ document_type: d.document_type, belongs_to: d.belongs_to, pages: d.pages || null, period: d.period_or_year || null }));
      await api.post(`/cases/${recordId}/documents/file`, {
        card_id: card.card_id, document_type: type, belongs_to: belongs,
        period: period || null, source, merge, password: password || null,
        pages: multi.length ? (multi[0].pages || null) : null, extra_documents });
      toast.success(extra_documents.length ? `Filed ${1 + extra_documents.length} documents from one file` : "Filed");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Filing failed"); }
    finally { setSaving(false); }
  };
  const Sel = ({ value, set, opts, ph, tid }) => (
    <select value={value} onChange={(e) => set(e.target.value)} data-testid={tid}
      className="w-full text-sm border border-[#E2E2E2] rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF4802]/40">
      <option value="">{ph}</option>{opts.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white rounded-2xl max-w-3xl" aria-describedby={undefined} data-testid="file-dialog">
        <DialogHeader><DialogTitle className="text-[#333333] font-medium">File document{card.original_name ? ` · ${card.original_name}` : ""}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="rounded-xl border border-[#E2E2E2] bg-[#F7F7F7] min-h-[260px] flex items-center justify-center overflow-hidden" data-testid="file-dialog-preview">
            <img src={`${BACKEND}/api/cases/${recordId}/documents/incoming/${card.card_id}/preview?full=1`} alt="Preview" className="max-h-[420px] w-full object-contain"
              onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement("div"), { className: "text-xs text-[#6B7280] p-4", textContent: s.needs_password ? "Locked PDF — enter the password to preview" : "No preview available" })); }} />
          </div>
          <div className="space-y-3">
          <div><label className="text-xs text-[#6B7280]">Document type</label><Sel value={type} set={setType} opts={typeOpts} ph="Pick type" tid="file-type" /></div>
          <div><label className="text-xs text-[#6B7280]">Belongs to</label><Sel value={belongs} set={setBelongs} opts={BELONGS} ph="Pick" tid="file-belongs" /></div>
          <div><label className="text-xs text-[#6B7280]">Period / Year</label><Input value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="file-period" className="border-[#E2E2E2] rounded-lg" placeholder="e.g. Apr–Sep 2026" /></div>
          <div><label className="text-xs text-[#6B7280]">Source</label><Sel value={source} set={setSource} opts={SOURCES} ph="Source" tid="file-source" /></div>
          {(isPdf || s.password_protected || s.needs_password) && (
            <div><label className="text-xs text-[#6B7280]">PDF password (if protected)</label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="file-password" className="border-[#E2E2E2] rounded-lg" placeholder="Leave blank if none" /></div>
          )}
          {isBurst && (
            <label className="flex items-center gap-2 text-sm text-[#333333]">
              <input type="checkbox" className="accent-[#FF4802] w-4 h-4" checked={merge} onChange={(e) => setMerge(e.target.checked)} data-testid="file-merge" />
              Merge {card.count} photos into one PDF (uncheck to file separately)
            </label>
          )}
          {multi.length > 1 && (
            <div className="rounded-lg border border-[#F5B71D]/40 bg-[#F5B71D]/10 p-2.5 text-xs" data-testid="file-multi-docs">
              <div className="font-medium text-[#92600a] mb-1">AI found {multi.length} documents in this file{multi[0].pages?.length ? ` — this row covers page${multi[0].pages.length > 1 ? "s" : ""} ${multi[0].pages.join(", ")}` : ""}. Also file:</div>
              {multi.slice(1).map((d, i) => (
                <label key={i} className="flex items-center gap-2 text-[#333333] py-0.5">
                  <input type="checkbox" className="accent-[#FF4802] w-4 h-4" checked={!!extraSel[i]} onChange={(e) => setExtraSel(extraSel.map((v, j) => (j === i ? e.target.checked : v)))} data-testid={`file-multi-${i}`} />
                  {d.pages?.length ? `p${d.pages.join(",")} · ` : ""}{d.document_type} · {d.belongs_to}{d.period_or_year ? ` · ${d.period_or_year}` : ""}
                </label>
              ))}
              <div className="text-[#6B7280] mt-1">Each becomes its own Documents row with the same file attached and the page noted.</div>
            </div>
          )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-[#E2E2E2] text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="file-confirm" className="px-4 py-2 rounded-lg bg-[#FF4802] text-white text-sm font-medium disabled:opacity-50">{saving ? "Filing…" : "File"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AllFiles({ recordId, refreshKey, onChanged }) {
  const [docs, setDocs] = useState(null);
  const load = async () => { try { const r = await api.get(`/cases/${recordId}/documents`); setDocs(r.data.documents || []); } catch { setDocs([]); } };
  useEffect(() => { load(); }, [recordId, refreshKey]); // eslint-disable-line
  const setStatus = async (id, status, reason) => {
    try { await api.post(`/cases/${recordId}/documents/${id}/status`, { status, reason: reason || null }); toast.success(status); load(); onChanged?.(); }
    catch { toast.error("Failed"); }
  };
  const askReshare = async (id, line) => {
    const reason = line.replace(/:\s*FAIL/i, "").trim() || line;
    try { await api.post(`/cases/${recordId}/documents/${id}/reshare`, { reason }); toast.success("Marked for re-share"); load(); onChanged?.(); }
    catch { toast.error("Failed"); }
  };
  if (!docs) return null;
  const groups = {};
  docs.forEach((d) => { const k = d.document_type || "Other"; (groups[k] = groups[k] || []).push(d); });
  const groupNames = Object.keys(groups).sort();
  return (
    <div className="rounded-2xl border border-[#E2E2E2] bg-white shadow-sm overflow-hidden" data-testid="all-files">
      <div className="px-5 py-3 bg-[#F7F7F7] border-b border-[#E2E2E2]"><h3 className="text-sm font-medium text-[#333333]">All files ({docs.length}) <Info k="verify_supersede" /></h3></div>
      <div className="divide-y divide-[#F7F7F7] max-h-[460px] overflow-y-auto">
        {docs.length === 0 && <div className="px-5 py-5 text-sm text-[#6B7280]">No documents filed yet.</div>}
        {groupNames.map((g) => (
          <div key={g} data-testid={`doc-group-${g.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="px-5 py-1.5 bg-[#FAFAFA] text-[11px] font-semibold uppercase tracking-wide text-[#8A8A8A]">{g} ({groups[g].length})</div>
            {groups[g].map((d) => {
              const failLines = (d.check_details || "").split("\n").filter((l) => /:\s*FAIL/i.test(l));
              return (
                <div key={d.id} className="px-5 py-2.5" data-testid={`doc-row-${d.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm text-[#333333] truncate" data-testid={`doc-name-${d.id}`}>{d.original_name || d.file_name || d.document || d.document_type}</div>
                      <div className="text-[11px] text-[#6B7280]">
                        {d.belongs_label}{d.period ? ` · ${d.period}` : ""}
                        {d.received_at ? ` · ${fmt(d.received_at)}` : ""}
                        {` · ${d.status}`}{d.source ? ` · ${d.source}` : ""}
                        {d.check_result && d.check_result !== "Not checked" ? ` · check: ${d.check_result}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {d.file_url && <a href={d.file_url} target="_blank" rel="noreferrer" className="text-xs text-[#38A2FF] hover:underline">Open</a>}
                      {d.status !== "Verified" && <button onClick={() => setStatus(d.id, "Verified")} data-testid={`verify-${d.id}`} className="text-xs text-[#159A50] hover:underline">Verify</button>}
                      {d.status !== "Superseded" && <button onClick={() => setStatus(d.id, "Superseded")} data-testid={`supersede-${d.id}`} className="text-xs text-[#6B7280] hover:underline">Supersede</button>}
                    </div>
                  </div>
                  {failLines.map((ln, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 mt-1 ml-1">
                      <span className="text-[11px] text-[#CA1212]">{ln}</span>
                      <button onClick={() => askReshare(d.id, ln)} data-testid={`reshare-${d.id}-${i}`} className="text-[10px] font-medium text-[#FF4802] hover:underline flex-shrink-0">Ask customer to re-share</button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export function DocumentCheckPanel({ recordId, c, onChanged, onRequestDocs }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState({});
  const timer = useRef(null);
  const load = async () => {
    try {
      const r = await api.get(`/cases/${recordId}/documents/check`);
      setRes(r.data);
      if (r.data.status === "running") { setBusy(true); timer.current = setTimeout(load, 3000); }
      else { if (busy && r.data.status === "done") { toast.success("Check complete"); onChanged?.(); } setBusy(false); }
    } catch { setBusy(false); }
  };
  useEffect(() => { load(); return () => clearTimeout(timer.current); }, [recordId]); // eslint-disable-line
  const run = async () => {
    setBusy(true);
    try { await api.post(`/cases/${recordId}/documents/check-all`); setRes((r) => ({ ...(r || {}), status: "running", done: 0 })); timer.current = setTimeout(load, 2500); }
    catch (e) { toast.error(e?.response?.data?.detail || "Check failed"); setBusy(false); }
  };
  const reshare = async (id, line) => {
    try { await api.post(`/cases/${recordId}/documents/${id}/reshare`, { reason: line }); toast.success("Marked for re-share"); onChanged?.(); load(); }
    catch { toast.error("Failed"); }
  };
  const sum = res?.summary;
  const Line = ({ k, label, count, tone, children }) => (
    <div className="border-t border-[#F7F7F7] first:border-0">
      <button onClick={() => setOpen((o) => ({ ...o, [k]: !o[k] }))} data-testid={`check-line-${k}`} className="w-full flex items-center justify-between py-2 text-sm">
        <span className={tone}>{count} {label}</span><span className="text-[11px] text-[#C5C4C4]">{open[k] ? "Hide" : "Show"}</span>
      </button>
      {open[k] && <div className="pb-2 space-y-1.5">{children}</div>}
    </div>
  );
  return (
    <div className="rounded-2xl border border-[#E2E2E2] bg-white shadow-sm overflow-hidden" data-testid="document-check">
      <div className="px-5 py-3 bg-[#F7F7F7] border-b border-[#E2E2E2] flex items-center justify-between">
        <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#159A50]" /><h3 className="text-sm font-medium text-[#333333]">Document check <Info k="doc_check" /></h3></div>
        <button onClick={run} disabled={busy} data-testid="check-all-btn" className="text-xs bg-[#222222] text-white rounded-lg px-3 py-1.5 font-medium hover:bg-[#333] disabled:opacity-50">{busy ? "Checking…" : "Check all documents"}</button>
      </div>
      <div className="p-5 space-y-3">
        {res?.status === "running" && (
          <div className="flex items-center gap-2 text-sm text-[#6B7280]" data-testid="check-progress"><Loader2 className="w-4 h-4 animate-spin text-[#FF4802]" /> Checking {Math.min((res.done || 0) + 1, res.total || 0)} of {res.total || 0}…</div>
        )}
        {res?.status === "failed" && (
          <div className="flex items-center justify-between text-sm text-[#CA1212]" data-testid="check-failed">Couldn't finish the check, try again
            <button onClick={run} data-testid="check-retry" className="text-xs text-[#FF4802] font-medium hover:underline">Retry</button></div>
        )}
        {!res?.status && <div className="text-sm text-[#6B7280]">Run a check to review all filed documents.</div>}
        {res?.status === "done" && sum && (
          <div data-testid="check-result-card">
            <div className="text-sm font-medium text-[#333333] mb-1" data-testid="check-headline">
              Checked {sum.checked} file{sum.checked === 1 ? "" : "s"} · {sum.passed.length} passed · {sum.issues.length} issue{sum.issues.length === 1 ? "" : "s"} · {sum.missing.length} missing{sum.unchecked ? ` · ${sum.unchecked} not checked` : ""}
            </div>
            <Line k="passed" label="passed" count={sum.passed.length} tone="text-[#159A50]">
              {sum.passed.map((p) => <div key={p.id} className="text-xs text-[#333333] ml-1">✅ {p.name} <span className="text-[#6B7280]">· {p.type} · {p.who}</span></div>)}
            </Line>
            <Line k="issues" label="issues" count={sum.issues.length} tone="text-[#CA1212]">
              {sum.issues.map((d) => (
                <div key={d.id} className="ml-1" data-testid={`check-issue-${d.id}`}>
                  <div className="text-xs text-[#333333]">❌ {d.name} <span className="text-[#6B7280]">· {d.type} · {d.who}</span></div>
                  {d.fails.map((f, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 ml-4">
                      <span className="text-[11px] text-[#CA1212]">{f}</span>
                      {d.status !== "Needs re-upload" && <button onClick={() => reshare(d.id, f)} data-testid={`check-reshare-${d.id}-${i}`} className="text-[10px] font-medium text-[#FF4802] hover:underline flex-shrink-0">Ask customer to re-share</button>}
                    </div>
                  ))}
                  {d.status === "Needs re-upload" && <div className="ml-4 text-[10px] text-[#6B7280]">Re-share requested</div>}
                </div>
              ))}
            </Line>
            <Line k="missing" label="missing for this profile" count={sum.missing.length} tone="text-[#92600a]">
              {sum.missing.map((m, i) => <div key={i} className="text-xs text-[#333333] ml-1">• {m.label}{m.note ? <span className="text-[#6B7280]"> — {m.note}</span> : null} <span className="text-[#C5C4C4]">({m.applicant})</span></div>)}
              {sum.missing.length > 0 && onRequestDocs && <button onClick={onRequestDocs} data-testid="check-request-missing" className="mt-1 text-xs bg-[#FF4802] text-white rounded-lg px-3 py-1.5 font-medium">Request these documents</button>}
            </Line>
            {res.at && <div className="text-[10px] text-[#C5C4C4] mt-1">checked {fmt(res.at)}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

export function RequestDocsButton({ recordId, c, cl, waLink, user, openSignal }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState(null);
  const [tmplKey, setTmplKey] = useState(null);
  const pendingCount = ["borrower", "coborrower", "company"].reduce((n, ap) =>
    n + new Set((cl?.applicants?.[ap] || []).filter((it) => ["missing", "needs_reupload", "partial"].includes(it.status)).map((it) => it.field_id || it.label.split(":")[0].trim())).size, 0);
  const openDialog = async () => {
    try {
      const r = await api.get(`/cases/${recordId}/documents/request-message`);
      setMsg(r.data); setTmplKey(r.data.situation); setOpen(true);
    } catch { toast.error("Could not build the request"); }
  };
  useEffect(() => { if (openSignal) openDialog(); }, [openSignal]); // eslint-disable-line
  const cur = msg?.templates?.find((t) => t.key === tmplKey) || msg?.templates?.[0];
  const onSent = async () => { try { await api.post(`/cases/${recordId}/documents/request/sent`, { count: msg?.pending_count || 0, items: (msg?.pending_list || "").split("\n").filter((l) => /^\d+\./.test(l)) }); } catch { } };
  const switcher = msg && (
    <div className="flex flex-wrap items-center gap-1.5 text-[11px]" data-testid="request-template-switch">
      <span className="text-[#6B7280]">Template:</span>
      {msg.templates.map((t) => (
        <button key={t.key} onClick={() => setTmplKey(t.key)} data-testid={`request-tmpl-${t.key}`}
          className={`px-2 py-0.5 rounded-full border ${tmplKey === t.key ? "bg-[#FF4802] text-white border-[#FF4802]" : "border-[#E2E2E2] text-[#6B7280] hover:border-[#FF6F38]"}`}>{t.name}</button>
      ))}
      {msg.situation === "second" && msg.days_since_request != null && <span className="text-[#C5C4C4]">· last request {msg.days_since_request}d ago</span>}
    </div>
  );
  return (
    <>
      <button onClick={openDialog} disabled={pendingCount === 0} data-testid="request-docs-btn"
        className="flex items-center gap-1.5 text-sm bg-[#FF4802] text-white rounded-lg px-4 py-2 font-medium hover:bg-[#e03e00] disabled:opacity-40">
        <Send className="w-4 h-4" /> Request documents ({pendingCount})
      </button>
      {open && cur && <MessageDialog key={cur.key} tmpl={{ id: cur.id, name: cur.name }} initial={cur.body} recordId={recordId} name={msg.first_name} waOpenLink={waLink}
        header={switcher} onClose={() => setOpen(false)} onNoteAdded={onSent} />}
    </>
  );
}
